/**
 * All API calls should originate in this file. Each exported function
 * should return a Promise
 */
import axios from 'axios'
import FileSaver from 'file-saver'
import { flattenDeep } from 'lodash'
import format from 'date-fns/format'
import store from '@/store'
import age from './elasticsearch/LAS/age'
import broadcastDates from './elasticsearch/LAS/broadcastDates'
import historicalViewership from './elasticsearch/LAS/historicalViewership'
import income from './elasticsearch/LAS/income'
import metrics from './elasticsearch/LAS/metrics'
import targetDefinition from './elasticsearch/LAS/targetDefinition'
import universeEstimate from './elasticsearch/LAS/universeEstimate'
import ConfigAPI from '@/api/ConfigAPI'

export { default as NotificationAPI } from './NotificationAPI'
export { default as PermissionAPI } from './PermissionsAPI'

const API_ROOT_TARGET_MANAGER = process.env.API_ROOT
const API_V2_ROOT = process.env.API_V2_ROOT
const API_ROOT_TARGET_MANAGER_NODE = process.env.API_ROOT_NODE
const API_ROOT_LAS = process.env.API_ROOT_LAS
const API_ROOT_SM = process.env.API_ROOT_SM
const LOAD_BALANCER = process.env.API_LOAD_BALANCER
const API_FOR_XANDR = process.env.API_ROOT_XANDR_NODE

export const getHeaders = function () {
  const headers = {
    Authorization: JSON.parse(localStorage['okta-token-storage']).accessToken.accessToken,
    'X-Username': store.state.auth.username,
  }
  return headers
}

const runAuthorizationAPI = function (runFunc, timesRun) {
  timesRun = timesRun || 1
  if (timesRun > 15) {
    return runFunc()
  }
  return new Promise((resolve, reject) => {
    let hasToken = store.state.auth.token
    // since using OIDC getting the token may take some time do do
    if (hasToken) {
      resolve(runFunc())
    } else {
      setTimeout(() => {
        resolve(runAuthorizationAPI(runFunc, timesRun + 1))
      }, 150)
    }
  })
}

const postAuthorization = function (url, payload) {
  return runAuthorizationAPI(() => {
    return axios.post(url, payload, {
      headers: getHeaders(),
    })
  })
}

const putAuthorization = function (url, payload) {
  return runAuthorizationAPI(() => {
    return axios.put(url, payload, {
      headers: getHeaders(),
    })
  })
}

const getAuthorization = function (url, params = {}) {
  return runAuthorizationAPI(() => {
    return axios.get(url, {
      ...params,
      headers: getHeaders(),
    })
  })
}

const deleteAuthorization = function (url) {
  return runAuthorizationAPI(() => {
    return axios.delete(url, {
      headers: getHeaders(),
    })
  })
}

export const authorizedAPIs = {
  post: postAuthorization,
  put: putAuthorization,
  get: getAuthorization,
  delete: deleteAuthorization,
}

/**
 * 1. enter the application
 * 2. get the store from the session
 *   a. if the session does not exist, then load redirect to the front page
 *   b. update the store if the session exists
 * 4. user will sign in
 * 5. if successful, then the session will be updated with the values
 *    a. the store will be updated as well
 */

export function refreshToken() {
  let url = `${API_ROOT_TARGET_MANAGER}/RefreshToken`
  return axios.post(url, { refresh_token: store.state.auth.refreshToken })
}

export function refreshIDMToken() {
  let url = `${API_ROOT_TARGET_MANAGER_NODE}/IDMToken/Refresh`
  return postAuthorization(url, { refreshToken: store.state.idmAuth.refreshToken }).then(resp => resp.data)
}

export function getUserPermissions() {
  let url = `${API_ROOT_TARGET_MANAGER_NODE}/UserPermissions`
  return postAuthorization(url, { username: store.state.auth.username })
}

export function fetchSMTargetConfig() {
  return ConfigAPI.getConfigFilesForConfigType('sm-target-config.json').fetchConfig()
}

export function fetchNetworksConfig() {
  return ConfigAPI.getConfigFilesForConfigType('networks').fetchConfig()
}

export function login(credentials) {
  return axios.post(`${API_ROOT_TARGET_MANAGER_NODE}/Login`, credentials)
}

export async function fetchEsDateRanges(dataSource = 'amrld') {
  const result = await broadcastDates(dataSource)
  const aggs = result.data.aggregations
  const dateRanges = aggs.months.buckets.map(m => ({
    name: m.key_as_string,
    from: m.start_date.value_as_string,
    to: m.end_date.value_as_string,
  }))
  const mriLatestMonth = aggs.mri.latest_month.value_as_string
  const ncsLatestMonth = aggs.ncs.latest_month.value_as_string
  return { dateRanges, mriLatestMonth, ncsLatestMonth }
}

export function fetchPostData(args) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/Audience/PostInfo`, args).catch(function (error) {
    console.log(error)
  })
}

export function assignOpenAPTarget({ target_id, assignor, owner }) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/OpenAP/AssignTarget`, {
    target_id,
    assignor,
    username: owner,
  }).catch(function (error) {
    console.log(error)
  })
}

export function importOpenAPTarget({
  segment_id,
  username,
  target_id,
  viewing_stream,
  advertiser,
  history_start_date,
  history_end_date,
}) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/OpenAP/ImportTarget`, {
    segment_id,
    username,
    target_id,
    viewing_stream,
    advertiser,
    history_start_date,
    history_end_date,
  }).catch(function (error) {
    console.log(error)
  })
}

export function saveOpenAPTarget({
  name,
  username,
  segment_id,
  viewing_stream,
  advertiser,
  history_start_date,
  history_end_date,
}) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/OpenAP/SaveTarget`, {
    name,
    username,
    segment_id,
    viewing_stream,
    advertiser,
    history_start_date,
    history_end_date,
  }).catch(function (error) {
    console.log(error)
  })
}

export function getOpenAPTarget(segmentId) {
  return getAuthorization(`${API_ROOT_TARGET_MANAGER}/OpenAP/Target?target_id=${segmentId}`)
}

export async function fetchTargetMetrics(args) {
  return await metrics(args)
}

export function fetchTargetAudienceSize(args) {
  return postAuthorization(`${API_ROOT_LAS}/LAS/TargetAudience`, args).catch(function (error) {
    console.log(error)
  })
}

export async function fetchTargetHistoricalViewership(args) {
  return await historicalViewership(args)
}

export function callSegmentation(promiseCall, data, segmentLimit) {
  const collectionOfResponses = []
  const turns = []
  for (let i = 0; i < data.length; i += segmentLimit) {
    turns.push(data.slice(i, i + segmentLimit))
  }
  return callPromiseSegment(promiseCall, turns, 0, collectionOfResponses)
}

const callPromiseSegment = function (promise, turns, currentTurn, responses) {
  return promise(turns[currentTurn]).then(resp => {
    responses.push(resp)
    currentTurn += 1
    if (turns.length === currentTurn) {
      return responses
    } else {
      return callPromiseSegment(promise, turns, currentTurn, responses)
    }
  })
}

export async function fetchTargetUniverse(args) {
  return await universeEstimate(args)
}

export async function fetchTargetDefinition({ startDate, endDate, query, viewSource }) {
  let args = convertLasArgsToRequestParams({ startDate, endDate, query, viewSource })
  return await targetDefinition(args)
}

export async function fetchAgeDistribution({ query, startDate, endDate, viewSource }) {
  let args = convertLasArgsToRequestParams({ startDate, endDate, query, viewSource })
  return await age(args)
}

export async function fetchIncomeDistribution({ query, startDate, endDate, viewSource }) {
  let args = convertLasArgsToRequestParams({ startDate, endDate, query, viewSource })
  return await income(args)
}

export function fetchTargetData({ query, startDate, endDate, networks, viewSource }) {
  return axios
    .all([
      fetchTargetMetrics(convertLasArgsToRequestParams({ startDate, endDate, query, viewSource })),
      fetchTargetHistoricalViewership(
        convertLasArgsToRequestParams({ startDate, endDate, query, networks, viewSource }),
      ),
      fetchTargetUniverse(convertLasArgsToRequestParams({ startDate, endDate, query })),
    ])
    .then(
      axios.spread(
        (
          metricsResp = { data: undefined },
          historicalResp = { data: undefined },
          universeResp = { data: undefined },
        ) => {
          return {
            metrics: metricsResp.data,
            audienceSize: universeResp.data,
            historicalViewership: historicalResp.data,
            universe: universeResp.data,
          }
        },
      ),
    )
}

export function fetchAdTargetMetrics(args) {
  return postAuthorization(`${API_ROOT_LAS}/AD/Metrics`, args).catch(function (error) {
    console.log(error)
  })
}

export function fetchAdTargetAudienceSize(args) {
  return postAuthorization(`${API_ROOT_LAS}/AD/TargetAudience`, args).catch(function (error) {
    console.log(error)
  })
}

export function fetchAdTargetHistoricalViewership(args) {
  return postAuthorization(`${API_ROOT_LAS}/AD/HistoricalViewership`, args).catch(function (error) {
    console.log(error)
  })
}

export function fetchAdTargetUniverse(args) {
  return postAuthorization(`${API_ROOT_LAS}/AD/UniverseEstimate`, args).catch(function (error) {
    console.log(error)
  })
}

export function fetchAdTargetData({ query, startDate, endDate, networks, viewSource }) {
  return axios
    .all([
      fetchAdTargetMetrics(convertLasArgsToRequestParams({ startDate, endDate, query, viewSource })),
      fetchAdTargetAudienceSize(convertLasArgsToRequestParams({ startDate, endDate, query, viewSource })),
      fetchAdTargetHistoricalViewership(
        convertLasArgsToRequestParams({ startDate, endDate, query, networks, viewSource }),
      ),
      fetchAdTargetUniverse(convertLasArgsToRequestParams({ startDate, endDate, query, viewSource })),
    ])
    .then(
      axios.spread(
        (
          metricsResp = { data: undefined },
          audienceSizeResp = { data: undefined },
          historicalResp = { data: undefined },
          universeResp = { data: undefined },
        ) => {
          return {
            metrics: metricsResp.data,
            audienceSize: audienceSizeResp.data,
            historicalViewership: historicalResp.data,
            universe: universeResp.data,
          }
        },
      ),
    )
}

export function fetchSmTargetData(definition) {
  definition = {
    query: definition.query,
    view_source: definition.viewSource,
  }
  return axios
    .all([
      postAuthorization(`${API_ROOT_SM}/SM/Metrics`, definition).catch(function (error) {
        console.log(error)
      }),
      postAuthorization(`${API_ROOT_SM}/SM/Audience`, definition).catch(function (error) {
        console.log(error)
      }),
      postAuthorization(`${API_ROOT_SM}/SM/TspAttributes`, definition).catch(function (error) {
        console.log(error)
      }),
      postAuthorization(`${API_ROOT_SM}/SM/SocialAffinity`, definition).catch(function (error) {
        console.log(error)
      }),
      postAuthorization(`${API_ROOT_SM}/SM/Reach`, definition).catch(function (error) {
        console.log(error)
      }),
    ])
    .then(
      axios.spread(
        (
          metricsResp = { data: undefined },
          audienceSizeResp = { data: undefined },
          topTspResp = { data: undefined },
          topSocialAffinityResp = { data: undefined },
          reachResp = { data: undefined },
        ) => {
          return {
            metrics: metricsResp.data,
            audienceSize: audienceSizeResp.data,
            topTsp: topTspResp.data,
            topSocialAffinity: topSocialAffinityResp.data,
            reach: reachResp.data,
          }
        },
      ),
    )
}

function convertLasArgsToRequestParams({ startDate, endDate, query, networks, viewSource }) {
  return { 'start-date': startDate, 'end-date': endDate, query, networks, view_source: viewSource }
}

export function fetchTargets(agGridModel, username = '', archived = false) {
  return postAuthorization(
    `${API_ROOT_TARGET_MANAGER}/Audience/Targets${username ? `/${username}` : ''}?archived=${archived}`,
    agGridModel,
  )
}

/**
 * fetches a single target, and all the information for that target
 * @param {target_id} just
 */
export function fetchSingleTarget({ target_id }) {
  return getAuthorization(`${API_V2_ROOT}/Targets/${target_id}`)
}

export function getTargetHistory(target_id) {
  return getAuthorization(`${API_V2_ROOT}/Targets/${target_id}/Revisions`)
}

export function appendTargetHistory(target_id, { branch_id, move, target_definition, label, target_data, audience }) {
  return postAuthorization(`${API_V2_ROOT}/Targets/${target_id}/Revisions`, {
    branch_id,
    move,
    target_definition,
    label,
    target_data,
    audience,
  })
}

export function searchAdvertisers(search) {
  return getAuthorization(`${API_V2_ROOT}/Advertisers/${search}`)
}

export function fetchMarketingNetworks() {
  return getAuthorization(`${API_ROOT_TARGET_MANAGER}/Networks`).then(resp => {
    return resp.data
  })
}

export function deleteTarget(target) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/UpdateTargetStatus`, {
    field: 'deleted',
    target: target,
  })
}

export function parseNPowerFile({ bucket, key }) {
  return postAuthorization(`${LOAD_BALANCER}/npower/parsefile`, { bucket, key })
}

export function parseComscoreFile({ bucket, key }) {
  return postAuthorization(`${LOAD_BALANCER}/comscore/parsefile`, { bucket, key })
}

export function uploadNPowerSegments(params) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/UploadNPowerSegments`, {
    segmentData: params.segmentData,
    target_id: params.target_id,
    marketDemos: params.marketDemos,
  })
}

export function sendNPowerDownstream(params) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/SendNPowerDownstream`, {
    target_id: params.target_id,
  })
}

export function sendComscoreDownstream(params) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/SendComscoreDownstream`, {
    target_id: params.target_id,
  })
}

export function uploadComscoreSegments(params) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/UploadComscoreSegments`, {
    segmentData: params.segmentData,
    target_id: params.target_id,
    username: params.username,
    marketDemos: params.marketDemos,
  })
}

export function updateTargetUE(params) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/UpdateTargetUE`, {
    targetId: params.targetId,
    ue: params.ue,
  })
}

export function archiveTarget(target) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/UpdateTargetStatus`, {
    field: 'archived',
    target: target,
  }).catch(err => {
    return { Error: (err.response && err.response.data && err.response.data) || err }
  })
}

export function unArchiveTarget(target) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/UpdateTargetStatus`, {
    field: 'unarchived',
    target: target,
  })
}

export function createTarget(target) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/Audience/SaveTarget`, { target })
}

export function saveTarget(target) {
  return putAuthorization(`${API_V2_ROOT}/Targets/${target.id}`, target)
}

export function activateTarget(target) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/Audience/Activate`, { target })
}

export function duplicateTarget({ target, duplicate_id }) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/Audience/Duplicate`, { target, duplicate_id })
}

export function submitToDC(target) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/SubmitToDC`, target)
}

export function sendToLake5(target) {
  return postAuthorization(`${API_V2_ROOT}/airflow/Lake5/${target.id}`, target)
}

export function getTargetUploadUrl(params) {
  return getAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/GetTargetUploadUrl`, { params })
}

export function createTargetWithNoActuals(target) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/CreateTarget`, target)
}

/**
 * @param {{ username, display_name, advertiser, target_datasource, target, viewing_stream }} target
 * @param {extension} file
 */
export function uploadTargetFile({ target, file, options }) {
  options = options || {}
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/UploadTarget`, { target, file, options })
}

export function getUploadTargetData({ target_type, id }) {
  const url = `${API_ROOT_TARGET_MANAGER_NODE}/Audience/UploadTarget/${target_type}/${id}`
  return getAuthorization(url)
}

export function moveStagedFileToUpload({ target }) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/MoveStagedFile`, { target })
}

export function getTargetPidLists(id) {
  const url = `${API_ROOT_TARGET_MANAGER_NODE}/Audience/PIDLists/${id}`
  return getAuthorization(url)
}

export function downloadFile(url, filename) {
  return axios.get(url, { responseType: 'blob' }).then(r => FileSaver.saveAs(new Blob([r.data]), filename))
}

export function getTargetsByTargetType({ target_type }) {
  const url = `${API_ROOT_TARGET_MANAGER}/Audience/Targets?target_type=${target_type}`
  return getAuthorization(url)
}

export function deleteTargetResource({ target_id }) {
  const url = `${API_ROOT_TARGET_MANAGER}/Audience/DeleteTargetResource?target_id=${target_id}`
  return getAuthorization(url)
}

export function uploadFile(url, file) {
  // TODO need to supply the correct axios request
  return axios.put(url, file, { headers: { 'Content-Type': '' } })
}

export function exportTargetDefinition({ targetId, targetDefinition, audienceSize }) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/Audience/exportTargetDefinition`, {
    targetId,
    targetDefinition,
    audienceSize,
  }).then(response => {
    // response from API is base64 encoded string, convert string to data url to download
    const element = document.createElement('a')
    element.setAttribute('href', 'data:application/pdf;charset=utf-16le;base64,' + encodeURIComponent(response.data))
    element.setAttribute('download', 'target definition.pdf')
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  })
}

export function checkPIDComplete({ target_id }) {
  const url = `${API_ROOT_TARGET_MANAGER}/Audience/PID/checkFileResource`
  return postAuthorization(url, { target_id })
}

export function overwritePIDFiles({ target_id, overriddens }) {
  const url = `${API_ROOT_TARGET_MANAGER}/Audience/PID/OverWriteFiles`
  return postAuthorization(url, { target_id, overriddens })
}

export function editTarget(target) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/Audience/SaveTarget`, { target })
}

export function postTarget(target) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/Audience/Post`, target)
}

export function validateLASDataAvailable(targetId) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/Audience/ValidateLASDataAvailable`, { id: targetId })
}

export function validateTargetName(payload) {
  const query = payload.isCaseSensitive ? '?isCaseSensitive=true' : ''
  const url = `${API_ROOT_TARGET_MANAGER_NODE}/Audience/ValidateTargetName/${payload.display_name}` + query
  return getAuthorization(url)
}

export function validateCampaignName(campaignName) {
  const url = `${API_ROOT_TARGET_MANAGER_NODE}/Audience/ValidateCampaignName/${campaignName}`
  return getAuthorization(url)
}

export function getTargetResources(targetId) {
  const url = `${API_ROOT_TARGET_MANAGER_NODE}/Audience/Resources?targetId=${targetId}`
  return getAuthorization(url)
}

export function sendPostData(target) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/Audience/PostData`, target)
}

export function rePostTarget({ target_id, start_date }) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER}/Audience/RePost`, { target_id, start_date })
}

export function getDroppedAttributesByQuarter({ target_id }) {
  return getAuthorization(`${API_ROOT_TARGET_MANAGER}/Audience/Target/DroppedAttributes?target_id=${target_id}`)
}

export function logXandrAction(body) {
  return postAuthorization(`${API_FOR_XANDR}/Xandr/Log`, body)
}

export function getRoles() {
  return getAuthorization(`${API_V2_ROOT}/Roles`)
}

export function getPermissions() {
  return getAuthorization(`${API_V2_ROOT}/Permissions`)
}

export function getUserPermissionsWithAuth() {
  return getAuthorization(`${API_V2_ROOT}/UserPermissions`)
}

export function updateRolePermissions(role) {
  return postAuthorization(`${API_V2_ROOT}/Role`, role)
}

export function logIntoIDM(username, password) {
  return postAuthorization(`${API_ROOT_TARGET_MANAGER_NODE}/IDMToken`, { username, password }).then(resp => resp.data)
}

export function batchLimitMaxCalls(apiCall, batches, limitNumber) {
  const newBatches = []
  const responseCollection = []
  for (let i = 0; i < batches.length; i += limitNumber) {
    newBatches.push(batches.slice(i, i + limitNumber))
  }
  return __limitMaxCalls(apiCall, newBatches, 0, responseCollection)
}

export function getComscoreDemos() {
  return getAuthorization(`${API_V2_ROOT}/Datasets/LAS/comscore`).then(resp => resp.data)
}

const __limitMaxCalls = function (api, newBatches, currentBatch, responseCollection) {
  const promises = newBatches[currentBatch].map(data => api(data))
  return axios.all(promises).then(resps => {
    resps = flattenDeep(resps) // could be coming from a collection of apis,  this will ensure that it is flattened
    currentBatch += 1
    responseCollection.push.apply(responseCollection, resps)
    if (newBatches.length === currentBatch) {
      return responseCollection
    } else {
      return __limitMaxCalls(api, newBatches, currentBatch, responseCollection)
    }
  })
}

const restartAPIGenerator = function (api, dataSegment) {
  const restartAPI = data => {
    const halfPayload = function (apiFunc, data) {
      let segmentedData = data
      if (dataSegment) {
        segmentedData = data[dataSegment]
      }
      const half = segmentedData.length / 2
      let data1 = segmentedData.slice(0, half)
      let data2 = segmentedData.slice(half, segmentedData.length)
      if (dataSegment) {
        data1 = {
          ...data,
          [dataSegment]: data1,
        }
        data2 = {
          ...data,
          [dataSegment]: data2,
        }
      }
      const promise1 = restartAPI(data1)
      const promise2 = restartAPI(data2)
      return axios.all([promise1, promise2]).then(resps => {
        return flattenIfArray(resps)
      })
    }
    // here the api is the api that we always want to call...
    return __restartAPIcall(api, data, halfPayload).then(resps => {
      return flattenIfArray(resps)
    })
  }
  return restartAPI
}

const flattenIfArray = function (ar) {
  if (Array.isArray(ar)) {
    return flattenDeep(ar)
  } else {
    return ar
  }
}

const __restartAPIcall = function (api, data, restartFunction) {
  return api(data).catch(() => restartFunction(api, data))
}

const dataSourceArmldOnly = function (args) {
  let queryValue = args['query']
  let dataSource = new Set()
  checkArray(queryValue, dataSource)
  if (dataSource.has('household')) {
    dataSource.delete('household')
  }
  if (dataSource.has('respondent')) {
    dataSource.delete('respondent')
  }
  let armldOnly = dataSource.size <= 0
  return armldOnly
}

const checkArray = function (array, dataSource) {
  Array.from(array).forEach(element => {
    if (isObject(element)) {
      dataSource.add(element['dataset'])
    } else if (isArray(element)) {
      checkArray(element, dataSource)
    } else {
      // discard element
    }
  })
}

const isObject = function (arg) {
  return arg !== null && typeof arg === 'object' && !Array.isArray(arg)
}

const isArray = function (arg) {
  return arg && typeof arg === 'object' && arg.constructor === Array
}

const formatTargetToSend = function (target, action) {
  return {
    caeId: target.cae_id,
    username: target.username,
    submittedBy: store.state.auth.username,
    timestamp: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
    queryCounts: (target.xandr_data && target.xandr_data.query) || {},
    targetDefinition: target.target_definition.query,
    action: action,
    id: target.id,
  }
}



// WEBPACK FOOTER //
// ./src/app/api/index.js